// import node module libraries
import { Fragment, useEffect, useState, useRef } from 'react';
import { Accordion, Col, Row, Overlay, Popover, Breadcrumb, Card, Form, Button, Spinner, Table, NavDropdown } from 'react-bootstrap';
import { Trash, Plus, Clipboard } from 'react-feather';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useTranslation } from 'react-i18next';

// Import authentication components
import useAuthHeader from 'react-auth-kit/hooks/useAuthHeader';
import useAuthUser from 'react-auth-kit/hooks/useAuthUser';

// import notification component
import Notification, { handleShowNotification } from 'components/elements/notification/Notification';

// Import required custom components
import DotBadge from 'components/elements/bootstrap/DotBadge';
import { FormSelect } from 'components/elements/form-select/FormSelect';

const ConnectorMentionSettings = ({ activeKey }) => { // activeKey als Prop erhalten
	const { t } = useTranslation(); // useTranslation-Hook
	const [notification, setNotification] = useState({ variant: '', title: '', message: '' });
	const [isLoading, setIsLoading] = useState(true); // State for loading table
	const [isLoadingButton, setIsLoadingButton] = useState(false); // State for loading spinner
	const [isButtonDisabled, setIsButtonDisabled] = useState(false); // State for button disabled when loading
	const [customNotes, setCustomNotes] = useState([{ custom_note: '', custom_merchant_sku: '' }]);

	const auth = useAuthUser(); // Assuming this gets the auth user data
	const [formData, setFormData] = useState(null);
	const [data, setData] = useState(null); // Add data state

	useEffect(() => {
		// Check if the current tab is active before making the API call
		if (activeKey === 'connector.mention') {
			getData();
		}
	}, [activeKey]); // Run effect when activeKey changes

	// Update formData when data changes
	useEffect(() => {
		if (data?.data?.config_value?.custom_notes) {
			// Parse the string to an array
			try {
				const parsedNotes = data?.data?.config_value?.custom_notes;
				if (Array.isArray(parsedNotes)) {
					setCustomNotes(parsedNotes);
				} else {
					console.error('custom_notes is not an array after parsing:', parsedNotes);
					setCustomNotes([]); // Fallback to an empty array to prevent errors
				}
			} catch (error) {
				console.error('Error parsing custom_notes:', error);
				setCustomNotes([]); // Fallback to an empty array in case of parsing error
			}
		} else {
			setCustomNotes([]); // Ensure it's an array if data is empty
		}
	}, [data]);

	// API-Call Options
	const authHeader = useAuthHeader();
	const options = {
		params: {

		},
		headers: {
			Authorization: authHeader,
			Accept: 'application/json'
		}
	};

	// API-Call for all Data
	const getData = async () => {
		setIsLoading(true);

		// Dynamische URL basierend auf der Umgebung
		const baseURL = process.env.REACT_APP_ENV === 'development'
			? `http://${process.env.REACT_APP_BACKEND_API_BASE_URI_ENV}`
			: `https://${process.env.REACT_APP_BACKEND_API_BASE_URI_ENV}`;

		await axios.get(`${baseURL}/tenants/${auth.tenant_id}/configs?key=connector.mention`, options)
			.then(response => {
				const responseData = response.data.data[0]; // Extract the data from the response
				setData({ data: responseData });
				setFormData({
					id: responseData.id || '',
					config_key: responseData.config_key || '',
					config_value: responseData.config_value || '',
					
				});

				setIsLoading(false);
			})
			.catch((error) => {
				const errorMessage = error.response?.data?.message ? error.message + '. ' + error.response.data.message : error.message;
				handleShowNotification(setNotification, 'danger', 'Fehler', errorMessage + '. Bitte versuche es erneut oder kontaktiere den HelpDesk.');
				setIsLoading(false);
			});
	};

	const handleChange = (e) => {
		const { name, value } = e.target;

		setFormData(prevFormData => {
			// Ensure config_value is always an object
			const updatedConfigValue = { ...prevFormData.config_value, [name]: value };

			return {
				...prevFormData,
				config_value: updatedConfigValue
			};
		});
	};

	const handleCustomNoteChange = (index, event) => {
		const { name, value } = event.target;
		const list = [...customNotes];
		list[index][name] = value;
		setCustomNotes(list);

		// Update formData to reflect changes in customNotes
		setFormData(prevFormData => ({
			...prevFormData,
			config_value: {
				...prevFormData.config_value,
				custom_notes: list // Store as string
			}
		}));
	};

	const handleAddCustomNote = () => {
		setCustomNotes([...customNotes, { custom_note: '', custom_merchant_sku: '' }]);
	};

	const handleRemoveCustomNote = (index) => {
		const list = [...customNotes];
		list.splice(index, 1);
		setCustomNotes(list);

		// Update formData to reflect changes in customNotes
		setFormData(prevFormData => ({
			...prevFormData,
			config_value: {
				...prevFormData.config_value,
				custom_notes: list // Store as string
			}
		}));
	};

	const handleSubmit = async (e) => {
		e.preventDefault();
		setIsLoadingButton(true);

		// Dynamische URL basierend auf der Umgebung
		const baseURL = process.env.REACT_APP_ENV === 'development'
			? `http://${process.env.REACT_APP_BACKEND_API_BASE_URI_ENV}`
			: `https://${process.env.REACT_APP_BACKEND_API_BASE_URI_ENV}`;

		await axios.patch(`${baseURL}/tenants/${auth.tenant_id}/configs/${formData.id}`, { ...formData, config_value: JSON.stringify(formData.config_value) }, options)
			.then(response => {
				//setData({ data: response.data.data });
				setIsLoadingButton(false);
				handleShowNotification(setNotification, 'success', 'Erfolgreich', 'Deine Daten wurden erfolgreich gespeichert');
			})
			.catch((error) => {
				setIsLoadingButton(false);
				const errorMessage = error.response?.data?.message ? error.message + '. ' + error.response.data.message : error.message;
				handleShowNotification(setNotification, 'danger', 'Fehler', errorMessage + '. Bitte versuche es erneut oder kontaktiere den HelpDesk.');
			});
	};

	const copyToClipboard = () => {
		navigator.clipboard.writeText('@custom_notes');
		handleShowNotification(setNotification, 'success', 'Kopiert', 'Die Variable wurde in die Zwischenablage kopiert!');
	};

	const [selectedContainerInstance, setSelectedContainerInstance] = useState('');

	const containerInstances = [
		{ value: t('ABC'), label: t('ABC') },
		{ value: t('DEF'), label: t('DEF') },
		{ value: t('IHJ'), label: t('IHJ') },
		{ value: t('KLM'), label: t('KLM') }
	];

	const [showVariableMenu, setShowVariableMenu] = useState(false);
	const [selectedField, setSelectedField] = useState(null);
	const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });
	const inputRef = useRef(null);

	const mentionVariables = [
		{
			order_item: [
				{ value: "{{order_item.order_amount}}", label: "{{order_item.order_amount}}", description: t('Total amount from the order in your currency') },
				{ value: "{{order_item.return_carrier | -}}", label: "{{order_item.return_carrier | -}}", description: t('Shipping service provider that handles the return shipment') },
				{ value: "{{order_item.label_type | -}}", label: "{{order_item.label_type | -}}", description: t('Shipping label used for the return shipment') },
				{ value: "{{order_item.label_cost | -}}", label: "{{order_item.label_cost | -}}", description: t('Costs for the return shipment label') },
				{ value: "{{order_item.tracking_id | -}}", label: "{{order_item.tracking_id | -}}", description: t('Consignment number of the return shipment') },
				{ value: "{{order_item.return_reason | -}}", label: "{{order_item.return_reason | -}}", description: t('Reason for returning the product') },
				{ value: "{{order_item.currency_code | EUR}}", label: "{{order_item.currency_code | EUR}}", description: t('Currency code of the order. If none is specified, “EUR” will be displayed') },
			],
			order_info: [
				{ value: "{{order_info.amazon_rma_id}}", label: "{{order_info.amazon_rma_id}}", description: t('Unique Amazon RMA ID for the return') },
				{ value: "{{order_info.order_id}}", label: "{{order_info.order_id}}", description: t('Unique order number from the order') },
				{ value: "{{order_info.order_date}}", label: "{{order_info.order_date}}", description: t('Date of the original order') },
				{ value: "{{order_info.resolution | -}}", label: "{{order_info.resolution | -}}", description: t('Resolution about the return') },
				{ value: "{{order_info.return_type | -}}", label: "{{order_info.return_type | -}}", description: t('Type of Return') },
				{ value: "{{order_info.a_to_z_claim | -}}", label: "{{order_info.a_to_z_claim | -}}", description: t('Indicates if this return is an A-to-Z request') },
				{ value: "{{order_info.invoice_id}}", label: "{{order_info.invoice_id}}", description: t('Invoice number of the original order, extracted from Mention') },
				{ value: "{{order_info.payment_id}}", label: "{{order_info.payment_id}}", description: t('Payment id of the original order, extracted from Mention') },
				{ value: "{{order_info.transport_id}}", label: "{{order_info.transport_id}}", description: t('Transport id of the original order, extracted from Mention') },
			],
			custom: [
				{ value: "@custom_notes", label: "@custom_notes", description: t('Display user-defined note, depending on an article number in the invoice') },
			]
		},
	];

	const handleInputChange = (e) => {
		const { name, value } = e.target;
		handleChange(e); // Call the existing handleChange function

		if (value.includes('{{') && selectedField === name) {
			setShowVariableMenu(true);
			setMenuPosition({
				top: e.target.offsetTop + e.target.offsetHeight,
				left: e.target.offsetLeft,
			});
		} else {
			setShowVariableMenu(false);
		}
	};

	const handleVariableSelect = (variable) => {
		setFormData(prevFormData => {
			const currentValue = prevFormData.config_value[selectedField] || '';
			const lastIndex = currentValue.lastIndexOf('{{');
			const newValue = lastIndex > -1 ? currentValue.substring(0, lastIndex) + variable.value : currentValue + variable.value;

			return {
				...prevFormData,
				config_value: {
					...prevFormData.config_value,
					[selectedField]: newValue
				}
			};
		});
		setShowVariableMenu(false);
	};

	const renderVariableMenu = () => {
		if (!showVariableMenu) return null;

		return (
			<NavDropdown
				show={showVariableMenu}
				style={{
					position: 'absolute',
					top: menuPosition.top + 'px',
					left: menuPosition.left + 'px',
					zIndex: 1000,
				}}
				onToggle={(isOpen) => {
					if (!isOpen) {
						setShowVariableMenu(false);
					}
				}}
				className="dropdown-without-icon"
				
			>
				{mentionVariables.map((category, index) => (
					<div key={index}>
						<NavDropdown.Header className="p-2 ms-3 h5">
							{t('CHOOSE VARIABLE')}
						</NavDropdown.Header>
						{Object.keys(category).map((key) => (
							<NavDropdown key={key} title={key} className="p-2 ms-3 h5" drop="end">
								{category[key].map((variable) => (
									<NavDropdown.Item
										key={variable.value}
										onClick={() => handleVariableSelect(variable)}
										className="mb-3 p-1 d-inline-block"
									>
										<h6 className="dropdown-header text-primary">
											{variable.label}
										</h6>
										<p className="dropdown-text mb-0 text-muted text-wrap">
											{variable.description}
										</p>
									</NavDropdown.Item>
								))}
							</NavDropdown>
						))}
					</div>
				))}
			</NavDropdown>
		);
	};

	// 
	// Components
	//

	// Component Status-Badge
	const statusBadge = (value) => {
		return (
			<Fragment>
				<DotBadge
					bg={
						value.includes('warning')
							? 'warning'
							: value === 'running'
								? 'success'
								: value === 'new'
									? 'info'
									: value.includes('error')
										? 'danger'
										: 'info'
					}
				></DotBadge>
				{
					value.includes('warning')
						? t('Warning')
						: value === 'running'
							? t('Running')
							: value === 'new'
								? t('New')
								: value.includes('error')
									? t('Error')
									: t('Unkown Status')
				}
			</Fragment>
		);
	}

	return (
		<Fragment>
			{notification.show && (
				<Notification variant={notification.variant} title={notification.title} message={notification.message} show={notification.show} setNotification={setNotification} />
			)}
			<Row>
				<Col xl={{ offset: 1, span: 10 }} lg={{ offset: 1, span: 10 }} md={12}>
					<Card className="mb-4">
						<Card.Header className="d-flex align-items-center justify-content-between">
							<h4 className="mb-0">{t('Prerequisites')}</h4>
						</Card.Header>
						<Card.Body>
							{isLoading ? (
								<div className="d-flex justify-content-center">
									<Spinner animation="border" role="status">
										<span className="visually-hidden">Loading...</span>
									</Spinner>
								</div>
							) : (
								<Form>
									<Row className="">
										<Col lg={12} md={12} className="mt-1">
											<h3 className="mb-3 border-bottom pb-2">{t('Explanation')}</h3>
											<p>{t('To create workshop orders, the connector.mention requires second connector.ms-sql to extract information from your Microsoft SQL database, where Mention Warehouse System stores its data.')}</p>
										</Col>
										<Col lg={12} md={12} className="mt-3">
											<h3 className="mb-3 border-bottom pb-2">{t('Configration')}</h3>
											<p>{t('Choose your running Container Instance ID from the Dropdown below.')}</p>
										</Col>
										<Col lg={12} md={12} className="mb-3">
											<Form.Group className="mb-3" controlId="containerInstance">
												<Form.Label>
													{t('Container Instance ID')} <span className="text-danger">*</span>
												</Form.Label>
												<Form.Control
													as={FormSelect}
													placeholder={selectedContainerInstance ? selectedContainerInstance : t('Choose Container Instance')}
													options={containerInstances}
													value={selectedContainerInstance}
													onChange={(e) => setSelectedContainerInstance(e.target.value)}
													required
												/>
											</Form.Group>
										</Col>
									</Row>
									<Row>
										<Col lg={12} md={12}>
											<div className="d-flex justify-content-end mt-4">
												<Button variant="primary" type="submit" disabled={isLoadingButton}>
													{isLoadingButton ? (
														<>
															<Spinner animation="border" size="sm" className="me-2" />
															{t('Save')}
														</>
													) : (
														t('Save')
													)}
												</Button>
											</div>
										</Col>
									</Row>
								</Form>
							)}
						</Card.Body>
					</Card>
				</Col>
			</Row>
			<Row>
				<Col xl={{ offset: 1, span: 10 }} lg={{ offset: 1, span: 10 }} md={12}>
					<Card className="mb-4">
						<Card.Header className="d-flex align-items-center justify-content-between">
							<h4 className="mb-0">{t('openTrans Workshop Order Template')}</h4>
						</Card.Header>
						<Card.Body>
							{isLoading ? (
								<div className="d-flex justify-content-center">
									<Spinner animation="border" role="status">
										<span className="visually-hidden">Loading...</span>
									</Spinner>
								</div>
							) : (
								<Form onSubmit={handleSubmit}>
									<Row className="mb-4">
										<Col lg={4} md={4} className="">
											<h3 className="mb-3 pb-2">{t('General infos')}</h3>
										</Col>
										<Col lg={8} md={8} className="mt-4">
												<p>{t('General metadata required to create the openTrans file.')}</p>												
											<Row>
												<Col lg={8} md={8} className="mb-3 d-none">
													<Form.Group controlId="id">
														<Form.Label visuallyHidden >ID</Form.Label>
														<Form.Control
															type="text"
															value={formData?.id}
															onChange={handleChange}
															disabled
														/>
													</Form.Group>
												</Col>
												{/* Stammdaten / Basic Data */}
												<Col lg={6} md={12} className="mb-3">
													<Form.Group controlId="generatorinfo">
														<Form.Label>
															{t('generator_info')}{' '}
														</Form.Label>
														<Form.Control
															type="text"
															name="generator_info"
															value={formData?.config_value?.generator_info || ''}
															onChange={(e) => {
																setSelectedField('generator_info');
																handleInputChange(e);
															}}
														/>
													</Form.Group>
												</Col>
												<Col lg={6} md={12} className="mb-3">
													<Form.Group controlId="version">
														<Form.Label>{t('version')}</Form.Label>
														<Form.Control
															type="text"
															name="version"
															value={formData?.config_value?.version || ''}
															onChange={(e) => {
																setSelectedField('version');
																handleInputChange(e);
															}}
														/>
													</Form.Group>
												</Col>
												<Col lg={6} md={6} className="mb-3">
													<Form.Group controlId="destination">
														<Form.Label>{t('destination')}</Form.Label>
														<Form.Control
															type="text"
															name="destination"
															value={formData?.config_value?.destination || ''}
															onChange={(e) => {
																setSelectedField('destination');
																handleInputChange(e);
															}}
														/>
													</Form.Group>
												</Col>
											</Row>
										</Col>
									</Row>
									<Row className="mb-4">
										<Col lg={4} md={4} className="">
											<h3 className="mb-3 pb-2">{t('openTrans specific')}</h3>
										</Col>
										<Col lg={8} md={8} className="mt-4">
											<p>{t('Interruption of automatic processing in the target system.')}</p>	
											<Row>
												<Col lg={6} md={6} className="mb-3">
													<Form.Group controlId="stop_automatic_processing">
														<Form.Label>{t('stop_automatic_processing')}</Form.Label>
														<Form.Check
															type="checkbox"
															name="stop_automatic_processing"
															label={formData?.config_value?.stopmanualprocessing === 'Manual' ? 'Manual' : 'Automatic'}
															className="form-switch"
															checked={formData?.config_value?.stopmanualprocessing !== 'Manual'}
															onChange={(e) => {
																const value = e.target.checked ? 'Automatic' : 'Manual';
																setFormData(prevFormData => ({
																	...prevFormData,
																	config_value: {
																		...prevFormData.config_value,
																		stopmanualprocessing: value
																	}
																}));
															}}
														/>
													</Form.Group>
												</Col>
											</Row>
										</Col>
									</Row>
									<Row className="mb-4">
										<Col lg={4} md={4} className="">
											<h3 className="mb-3 pb-2">{t('Mention specific')}</h3>
										</Col>
										<Col lg={8} md={8} className="mt-4">
											<Row>
												<Col lg={6} md={6} className="mb-3">
													<Form.Group controlId="bsgara">
														<Form.Label>{t('bsgara')}</Form.Label>
														<Form.Control
															type="text"
															name="bsgara"
															value={formData?.config_value?.bsgara || ''}
															onChange={(e) => {
																setSelectedField('bsgara');
																handleInputChange(e);
															}}
														/>
													</Form.Group>
												</Col>
												<Col lg={6} md={6} className="mb-3">
													<Form.Group controlId="bwausrtyp">
														<Form.Label>{t('bwausrtyp')}</Form.Label>
														<Form.Control
															type="text"
															name="bwausrtyp"
															value={formData?.config_value?.bwausrtyp || ''}
															onChange={(e) => {
																setSelectedField('bwausrtyp');
																handleInputChange(e);
															}}
														/>
													</Form.Group>
												</Col>
												<Col lg={6} md={6} className="mb-3">
													<Form.Group controlId="bsbeakz">
														<Form.Label>{t('bsbeakz')}</Form.Label>
														<Form.Control
															type="text"
															name="bsbeakz"
															value={formData?.config_value?.bsbeakz || ''}
															onChange={(e) => {
																setSelectedField('bsbeakz');
																handleInputChange(e);
															}}
														/>
													</Form.Group>
												</Col>
												<Col lg={6} md={6} className="mb-3">
													<Form.Group controlId="bssperr">
														<Form.Label>{t('bssperr')}</Form.Label>
														<Form.Control
															type="text"
															name="bssperr"
															value={formData?.config_value?.bssperr || ''}
															onChange={(e) => {
																setSelectedField('bssperr');
																handleInputChange(e);
															}}
														/>
													</Form.Group>
												</Col>
												<Col lg={6} md={6} className="mb-3">
													<Form.Group controlId="bsbeltyp">
														<Form.Label>{t('bsbeltyp')}</Form.Label>
														<Form.Control
															type="text"
															name="bsbeltyp"
															value={formData?.config_value?.bsbeltyp || ''}
															onChange={(e) => {
																setSelectedField('bsbeltyp');
																handleInputChange(e);
															}}
														/>
													</Form.Group>
												</Col>
											</Row>
											<Row>
												<Col lg={12} md={12} className="mt-4">
													<h4 className="mb-3">{t('Reference text')}</h4>
												</Col>
												{/* Bezugsdaten / Reference Data */}
												<Col lg={12} md={12} className="mb-3">
													<Form.Group controlId="bsbezug1">
														<Form.Label>{t('bsbezug1')}</Form.Label>
														<Form.Control
															type="text"
															name="bsbezug1"
															value={formData?.config_value?.bsbezug1 || ''}
															onChange={(e) => {
																setSelectedField('bsbezug1');
																handleInputChange(e);
															}}
															placeholder={t('Type {{ to see available variables')}
														/>
													</Form.Group>
												</Col>
												<Col lg={12} md={12} className="mb-3">
													<Form.Group controlId="bsbezug2">
														<Form.Label>{t('bsbezug2')}</Form.Label>
														<Form.Control
															type="text"
															name="bsbezug2"
															value={formData?.config_value?.bsbezug2 || ''}
															onChange={(e) => {
																setSelectedField('bsbezug2');
																handleInputChange(e);
															}}
															placeholder={t('Type {{ to see available variables')}
														/>
													</Form.Group>
												</Col>
												<Col lg={12} md={12} className="mb-3">
													<Form.Group controlId="bsbezug3">
														<Form.Label>{t('bsbezug3')}</Form.Label>
														<Form.Control
															type="text"
															name="bsbezug3"
															value={formData?.config_value?.bsbezug3 || ''}
															onChange={(e) => {
																setSelectedField('bsbezug3');
																handleInputChange(e);
															}}
															placeholder={t('Type {{ to see available variables')}
														/>
													</Form.Group>
												</Col>
											</Row>
											<Row>
												<Col lg={12} md={12} className="mt-4">
													<h4 className="mb-3">{t('External Reference')}</h4>
												</Col>
												<Col lg={12} md={12} className="mb-3">
													<Form.Group controlId="bsextref1">
														<Form.Label>{t('bsextref1')}</Form.Label>
														<Form.Control
															type="text"
															name="bsextref1"
															value={formData?.config_value?.bsextref1 || ''}
															onChange={(e) => {
																setSelectedField('bsextref1');
																handleInputChange(e);
															}}
															placeholder={t('Type {{ to see available variables')}
														/>
													</Form.Group>
												</Col>
												<Col lg={12} md={12} className="mb-3">
													<Form.Group controlId="bsextref2">
														<Form.Label>{t('bsextref2')}</Form.Label>
														<Form.Control
															type="text"
															name="bsextref2"
															value={formData?.config_value?.bsextref2 || ''}
															onChange={(e) => {
																setSelectedField('bsextref2');
																handleInputChange(e);
															}}
															placeholder={t('Type {{ to see available variables')}
														/>
													</Form.Group>
												</Col>
												<Col lg={12} md={12} className="mb-3">
													<Form.Group controlId="bsextref3">
														<Form.Label>{t('bsextref3')}</Form.Label>
														<Form.Control
															type="text"
															name="bsextref3"
															value={formData?.config_value?.bsextref3 || ''}
															onChange={(e) => {
																setSelectedField('bsextref3');
																handleInputChange(e);
															}}
															placeholder={t('Type {{ to see available variables')}
														/>
													</Form.Group>
												</Col>
											</Row>
											<Row>
												<Col lg={12} md={12} className="mt-4">
													<h4 className="mb-3">{t('Text information')}</h4>
												</Col>
												{/* Textdaten / Text Data */}
												<Col lg={12} md={12} className="mb-3">
													<Form.Group controlId="bwtext1">
														<Form.Label>{t('bwtext1')}</Form.Label>
														<Form.Control
															type="text"
															name="bwtext1"
															value={formData?.config_value?.bwtext1 || ''}
															onChange={(e) => {
																setSelectedField('bwtext1');
																handleInputChange(e);
															}}
															placeholder={t('Type {{ to see available variables')}
														/>
													</Form.Group>
												</Col>
												<Col lg={12} md={12} className="mb-3">
													<Form.Group controlId="bwtext2">
														<Form.Label>{t('bwtext2')}</Form.Label>
														<Form.Control
															type="text"
															name="bwtext2"
															value={formData?.config_value?.bwtext2 || ''}
															onChange={(e) => {
																setSelectedField('bwtext2');
																handleInputChange(e);
															}}
															placeholder={t('Type {{ to see available variables')}
														/>
													</Form.Group>
												</Col>
												<Col lg={12} md={12} className="mb-3">
													<Form.Group controlId="bwtext3">
														<Form.Label>{t('bwtext3')}</Form.Label>
														<Form.Control
															type="text"
															name="bwtext3"
															value={formData?.config_value?.bwtext3 || ''}
															onChange={(e) => {
																setSelectedField('bwtext3');
																handleInputChange(e);
															}}
															placeholder={t('Type {{ to see available variables')}
														/>
													</Form.Group>
												</Col>
												<Col lg={12} md={12} className="mb-3">
													<Form.Group controlId="bwtext4">
														<Form.Label>{t('bwtext4')}</Form.Label>
														<Form.Control
															type="text"
															name="bwtext4"
															value={formData?.config_value?.bwtext4 || ''}
															onChange={(e) => {
																setSelectedField('bwtext4');
																handleInputChange(e);
															}}
															placeholder={t('Type {{ to see available variables')}
														/>
													</Form.Group>
												</Col>
												<Col lg={12} md={12} className="mb-3">
													<Form.Group controlId="bwtext5">
														<Form.Label>{t('bwtext5')}</Form.Label>
														<Form.Control
															type="text"
															name="bwtext5"
															value={formData?.config_value?.bwtext5 || ''}
															onChange={(e) => {
																setSelectedField('bwtext5');
																handleInputChange(e);
															}}
															placeholder={t('Type {{ to see available variables')}
														/>
													</Form.Group>
												</Col>
												<Col lg={12} md={12} className="mb-3">
													<Form.Group controlId="bwtext6">
														<Form.Label>{t('bwtext6')}</Form.Label>
														<Form.Control
															type="text"
															name="bwtext6"
															value={formData?.config_value?.bwtext6 || ''}
															onChange={(e) => {
																setSelectedField('bwtext6');
																handleInputChange(e);
															}}
															placeholder={t('Type {{ to see available variables')}
														/>
													</Form.Group>
												</Col>
												<Col lg={12} md={12} className="mb-3">
													<Form.Group controlId="bwtext7">
														<Form.Label>{t('bwtext7')}</Form.Label>
														<Form.Control
															type="text"
															name="bwtext7"
															value={formData?.config_value?.bwtext7 || ''}
															onChange={(e) => {
																setSelectedField('bwtext7');
																handleInputChange(e);
															}}
															placeholder={t('Type {{ to see available variables')}
														/>
													</Form.Group>
												</Col>
												<Col lg={12} md={12} className="mb-3">
													<Form.Group controlId="bwtext8">
														<Form.Label>{t('bwtext8')}</Form.Label>
														<Form.Control
															type="text"
															name="bwtext8"
															value={formData?.config_value?.bwtext8 || ''}
															onChange={(e) => {
																setSelectedField('bwtext8');
																handleInputChange(e);
															}}
															placeholder={t('Type {{ to see available variables')}
														/>
													</Form.Group>
												</Col>
											</Row>
											<Row>
												<Col lg={12} md={12} className="mt-4">
														<h4 className="mb-3">{t('Order Hint')}</h4>
												</Col>
												<Col lg={12} md={12} className="mb-3">
													<Form.Group controlId="bshinweis">
														<Form.Label>{t('bshinweis')}</Form.Label>
														<Form.Control
															type="text"
															name="bshinweis"
															value={formData?.config_value?.bshinweis || ''}
															onChange={(e) => {
																setSelectedField('bshinweis');
																handleInputChange(e);
															}}
															placeholder={t('Type {{ to see available variables')}
														/>
													</Form.Group>
												</Col>
											</Row>
											<Row>
												<Col lg={12} md={12} className="mt-4">
													<h4 className="mb-3">{t('Order Info')}</h4>
												</Col>
												{/* Bestelldaten / Order Data */}
												<Col lg={6} md={6} className="mb-3">
													<Form.Group controlId="order_id">
														<Form.Label>{t('order_id')}</Form.Label>
														<Form.Control
															type="text"
															name="order_id"
															value={formData?.config_value?.order_id || ''}
															onChange={(e) => {
																setSelectedField('order_id');
																handleInputChange(e);
															}}
															placeholder={t('Type {{ to see available variables')}
														/>
													</Form.Group>
												</Col>
												<Col lg={6} md={6} className="mb-3">
													<Form.Group controlId="order_date">
														<Form.Label>{t('order_date')}</Form.Label>
														<Form.Control
															type="text"
															name="order_date"
															value={formData?.config_value?.order_date || ''}
															onChange={(e) => {
																setSelectedField('order_date');
																handleInputChange(e);
															}}
															placeholder={t('Type {{ to see available variables')}
														/>
													</Form.Group>
												</Col>
												<Col lg={6} md={6} className="mb-3">
													<Form.Group controlId="currency_code">
														<Form.Label>{t('currency_code')}</Form.Label>
														<Form.Control
															type="text"
															name="currency_code"
															value={formData?.config_value?.currency_code || ''}
															onChange={(e) => {
																setSelectedField('currency_code');
																handleInputChange(e);
															}}
															placeholder={t('Type {{ to see available variables')}
														/>
													</Form.Group>
												</Col>
												<Col lg={6} md={6} className="mb-3">
													<Form.Group controlId="bwausr">
														<Form.Label>{t('bwausr')}</Form.Label>
														<Form.Control
															type="text"
															name="bwausr"
															value={formData?.config_value?.bwausr || ''}
															onChange={(e) => {
																setSelectedField('bwausr');
																handleInputChange(e);
															}}
															placeholder={t('Type {{ to see available variables')}
														/>
													</Form.Group>
												</Col>
												<Col lg={6} md={6} className="mb-3">
													<Form.Group controlId="epgrund">
														<Form.Label>{t('epgrund')}</Form.Label>
														<Form.Control
															type="text"
															name="epgrund"
															value={formData?.config_value?.epgrund || ''}
															onChange={(e) => {
																setSelectedField('epgrund');
																handleInputChange(e);
															}}
														/>
													</Form.Group>
												</Col>
												{/* Partnerdaten / Partner Data */}
												<Col lg={6} md={6} className="mb-3">
													<Form.Group controlId="buyer_aid">
														<Form.Label>{t('buyer_aid')}</Form.Label>
														<Form.Control
															type="text"
															name="buyer_aid"
															value={formData?.config_value?.buyer_aid || ''}
															onChange={(e) => {
																setSelectedField('buyer_aid');
																handleInputChange(e);
															}}
															placeholder={t('Type {{ to see available variables')}
														/>
													</Form.Group>
												</Col>
												<Col lg={6} md={6} className="mb-3">
													<Form.Group controlId="buyer_aid_type">
														<Form.Label>{t('buyer_aid_type')}</Form.Label>
														<Form.Control
															type="text"
															name="buyer_aid_type"
															value={formData?.config_value?.buyer_aid_type || ''}
															onChange={(e) => {
																setSelectedField('buyer_aid_type');
																handleInputChange(e);
															}}
															placeholder={t('Type {{ to see available variables')}
														/>
													</Form.Group>
												</Col>
												<Col lg={6} md={6} className="mb-3">
													<Form.Group controlId="buyer_party_id">
														<Form.Label>{t('buyer_party_id')}</Form.Label>
														<Form.Control
															type="text"
															name="buyer_party_id"
															value={formData?.config_value?.buyer_party_id || ''}
															onChange={(e) => {
																setSelectedField('buyer_party_id');
																handleInputChange(e);
															}}
															placeholder={t('Type {{ to see available variables')}
														/>
													</Form.Group>
												</Col>
												<Col lg={6} md={6} className="mb-3">
													<Form.Group controlId="supplier_aid">
														<Form.Label>{t('supplier_aid')}</Form.Label>
														<Form.Control
															type="text"
															name="supplier_aid"
															value={formData?.config_value?.supplier_aid || ''}
															onChange={(e) => {
																setSelectedField('supplier_aid');
																handleInputChange(e);
															}}
															placeholder={t('Type {{ to see available variables')}
														/>
													</Form.Group>
												</Col>
												<Col lg={6} md={6} className="mb-3">
													<Form.Group controlId="supplier_party_id">
														<Form.Label>{t('supplier_party_id')}</Form.Label>
														<Form.Control
															type="text"
															name="supplier_party_id"
															value={formData?.config_value?.supplier_party_id || ''}
															onChange={(e) => {
																setSelectedField('supplier_party_id');
																handleInputChange(e);
															}}
															placeholder={t('Type {{ to see available variables')}
														/>
													</Form.Group>
												</Col>
											</Row>
											<Row>
												<Col lg={12} md={12} className="mt-4">
													<h4 className="mb-3">{t('Payment Info')}</h4>
												</Col>
												{/* Zahlungsdaten / Payment Data */}
												<Col lg={6} md={6} className="mb-3">
													<Form.Group controlId="payment_id">
														<Form.Label>{t('payment_id')}</Form.Label>
														<Form.Control
															type="text"
															name="payment_id"
															value={formData?.config_value?.payment_id || ''}
															onChange={(e) => {
																setSelectedField('payment_id');
																handleInputChange(e);
															}}
															placeholder={t('Type {{ to see available variables')}
														/>
													</Form.Group>
												</Col>
												<Col lg={6} md={6} className="mb-3">
													<Form.Group controlId="payment_term">
														<Form.Label>{t('payment_term')}</Form.Label>
														<Form.Control
															type="text"
															name="payment_term"
															value={formData?.config_value?.payment_term || ''}
															onChange={(e) => {
																setSelectedField('payment_term');
																handleInputChange(e);
															}}
															placeholder={t('Type {{ to see available variables')}
														/>
													</Form.Group>
												</Col>
												<Col lg={6} md={6} className="mb-3">
													<Form.Group controlId="payment_term_type">
														<Form.Label>{t('payment_term_type')}</Form.Label>
														<Form.Control
															type="text"
															name="payment_term_type"
															value={formData?.config_value?.payment_term_type || ''}
															onChange={(e) => {
																setSelectedField('payment_term_type');
																handleInputChange(e);
															}}
														/>
													</Form.Group>
												</Col>
											</Row>
											<Row>
												<Col lg={12} md={12} className="mt-4">
													<h4 className="mb-3">{t('Delivery Info')}</h4>
												</Col>
												{/* Lieferdaten / Delivery Data */}
												<Col lg={12} md={12} className="mb-3">
													<Form.Group controlId="transport_id">
														<Form.Label>{t('transport_id')}</Form.Label>
														<Form.Control
															type="text"
															name="transport_id"
															value={formData?.config_value?.transport_id || ''}
															onChange={(e) => {
																setSelectedField('transport_id');
																handleInputChange(e);
															}}
															placeholder={t('Type {{ to see available variables')}
														/>
													</Form.Group>
												</Col>
												{/* Sonstige / Other */}
												<Col lg={12} md={12} className="mb-3">
													<Form.Group controlId="partial_shipment_allowed">
														<Form.Label>{t('partial_shipment_allowed')}</Form.Label>
														<Form.Check
															type="checkbox"
															name="partial_shipment_allowed"
															label={formData?.config_value?.partial_shipment_allowed === 'false' ? 'false' : 'true'}
															className="form-switch"
															checked={formData?.config_value?.partial_shipment_allowed !== 'false'}
															onChange={(e) => {
																const value = e.target.checked ? 'true' : 'false';
																setFormData(prevFormData => ({
																	...prevFormData,
																	config_value: {
																		...prevFormData.config_value,
																		partial_shipment_allowed: value
																	}
																}));
															}}
														/>
													</Form.Group>
												</Col>
											</Row>
										</Col>
									</Row>
									{/* Dropdown-Menu for Variables */}	
									{renderVariableMenu()}
									<Row>
										<Col lg={12} md={12}>
											<div className="d-flex justify-content-end mt-4">
												<Button variant="primary" type="submit" disabled={isLoadingButton}>
													{isLoadingButton ? (
														<>
															<Spinner animation="border" size="sm" className="me-2" />
															{t('Save')}
														</>
													) : (
														t('Save')
													)}
												</Button>
											</div>
										</Col>
									</Row>
								</Form>
							)}	
						</Card.Body>
					</Card>
				</Col>
			</Row>
			<Row>
				<Col xl={{ offset: 1, span: 10 }} lg={{ offset: 1, span: 10 }} md={12}>
					<Card className="mb-4">
						<Card.Header className="d-flex align-items-center justify-content-between">
							<h4 className="mb-0">{t('Custom Notes')}</h4>
						</Card.Header>
						<Card.Body>
							{isLoading ? (
								<div className="d-flex justify-content-center">
									<Spinner animation="border" role="status">
										<span className="visually-hidden">Loading...</span>
									</Spinner>
								</div>
							) : (
								<Form onSubmit={handleSubmit}>
									<Row>
										<Col lg={12} md={12} className="">
											<p>{t('You can add user-defined notes to the workshop order at any point if certain article numbers are included in the associated invoice.')}</p>												
										</Col>
									</Row>
									<Row>
										<Col lg={4} md={4} className="">
												<h3 className="mb-3 pb-2">{t('Example')}</h3>
										</Col>
										<Col lg={8} md={8} className="">
											<Row>
												<Col lg={12} md={12} className="mt-4">
													<p>{t('For example, if you have a fixed dummy article number in the invoice for a redeemed discount voucher, you can use this number to display a note anywhere in the workshop order.')}</p>
												</Col>
											</Row>
										</Col>
									</Row>
									<Row>
										<Col lg={4} md={4} className="">
											<h3 className="mb-3 pb-2">{t('Manual')}</h3>
										</Col>
										<Col lg={8} md={8} className="">
											<Row>
												<Col lg={12} md={12} className="mt-4">
													<h4 className="mb-2">1. {t('Define item number and note')}</h4>
													<p>{t('First, simply define for every item number a custom note/reference to be displayed.')}</p>
													<Table size="sm">
														<thead>
															<tr>
																<th>{t('Merchant SKU')}</th>
																<th>{t('Custom Note')}</th>
																<th>{t('Action')}</th>
															</tr>
														</thead>
														<tbody>
															{customNotes.map((note, index) => (
																<tr key={index}>
																	<td>
																		<Form.Control
																			type="text"
																			name="custom_merchant_sku"
																			placeholder={t("Enter Merchant SKU")}
																			className="rounded"
																			size="sm"
																			value={note.custom_merchant_sku || ''}
																			onChange={event => handleCustomNoteChange(index, event)}
																		/>
																	</td>
																	<td>
																		<Form.Control
																			type="text"
																			name="custom_note"
																			placeholder={t("Enter Custom Note")}
																			className="rounded"
																			size="sm"
																			value={note.custom_note || ''}
																			onChange={event => handleCustomNoteChange(index, event)}
																		/>
																	</td>
																	<td>
																		<Button className="btn btn-sm btn-light rounded" onClick={() => handleRemoveCustomNote(index)}>
																			<Trash size="15px" className="" /> {t('Delete Note')}
																		</Button>
																	</td>
																</tr>
															))}
															<tr>
																<td colSpan="3">
																	<Button className="btn btn-sm btn-secondary rounded" onClick={handleAddCustomNote}>
																		<Plus size="15px" className="" /> {t('Add Note')}
																	</Button>
																</td>
															</tr>
														</tbody>
													</Table>
												</Col>
												<Col lg={12} md={12} className="mt-4">
													<h4 className="mb-2">2. {t('Map note to specific field')}</h4>
													<p>{t('Map your generated notes with the variable')}{' '}<code>@custom_notes</code>{' '}<Button size="sm" className="rounded btn-light" onClick={copyToClipboard}>@custom_notes{' '}<Clipboard size="15px" className="" /></Button>{' '}{t('to the field you prefer.')}</p>
												</Col>
											</Row>
										</Col>
									</Row>
									<Row>
										<Col lg={12} md={12}>
											<div className="d-flex justify-content-end mt-4">
												<Button variant="primary" type="submit" disabled={isLoadingButton}>
													{isLoadingButton ? (
														<>
															<Spinner animation="border" size="sm" className="me-2" />
															{t('Save')}
														</>
													) : (
														t('Save')
													)}
												</Button>
											</div>
										</Col>
									</Row>
								</Form>
							)}	
						</Card.Body>
					</Card>
				</Col>
			</Row>
		</Fragment>
	);
};

export default ConnectorMentionSettings;
